import api, { prefs as prefsApi, vatsimId as vatsimApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { TEXT_SIZES, canSetTextSize, getTextSize, setTextSize } from "@/lib/text-size";
import OverlayHotkeys from "@/components/OverlayHotkeys";

export default function Settings() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const { data: prefs } = useQuery({ queryKey: ["prefs"], queryFn: prefsApi.get });
  const { data: vatsim } = useQuery({ queryKey: ["vatsim-id"], queryFn: vatsimApi.get });
  const [vatsimCid, setVatsimCid] = useState("");
  useEffect(() => { if (vatsim) setVatsimCid(vatsim.cid || ""); }, [vatsim]);
  const vatsimInvalid = vatsimCid !== "" && !/^[0-9]{6,7}$/.test(vatsimCid);

  // Rank title, not the raw pilots.rank value. `user.rank` is the §17 index
  // ("6"), which is meaningless to a pilot — the dashboard already resolves it
  // through the profile card, and this page was showing the number.
  //
  // Same queryKey as PilotHeader, so react-query serves it from cache rather
  // than issuing a second request.
  const { data: card } = useQuery({
    queryKey: ["profile-card", user?.pid],
    queryFn: () => api.get("/pilot/profile-card").then((r) => r.data),
    enabled: !!user?.pid,
    staleTime: 60 * 60 * 1000,
    retry: false,
  });
  const [sbUserid, setSbUserid] = useState("");
  const [autoFile, setAutoFile] = useState(true);
  const [shareDebriefs, setShareDebriefs] = useState(true);
  const isDesktop = typeof window !== "undefined" && !!window.electronAPI;
  const [overlayEnabled, setOverlayEnabled] = useState(true);
  useEffect(() => {
    if (prefs) {
      setSbUserid(prefs.simbrief_userid || "");
      setAutoFile(prefs.auto_file_pirep ?? true);
      setShareDebriefs(prefs.share_debriefs ?? true);
    }
  }, [prefs]);
  useEffect(() => {
    if (!isDesktop) return;
    window.electronAPI.prefs.get().then((p) => {
      if (p && Object.prototype.hasOwnProperty.call(p, "overlayEnabled")) {
        setOverlayEnabled(!!p.overlayEnabled);
      }
    });
    // The hide/show hotkey and the tray can change it while this page is open.
    // Optional chaining: a desktop shell older than this page has no onChanged.
    return window.electronAPI.overlay?.onChanged?.((st) => setOverlayEnabled(!!st.enabled));
  }, [isDesktop]);

  const toggleOverlay = (next) => {
    setOverlayEnabled(next);
    if (isDesktop) {
      // Both channels stay in sync: prefs:set persists the value, overlay:setEnabled
      // also nudges the main process to show/hide immediately.
      window.electronAPI.prefs.set({ overlayEnabled: next });
      window.electronAPI.overlay.setEnabled(next);
    }
  };

  const savePrefs = useMutation({
    mutationFn: async () => {
      if (vatsimInvalid) throw new Error("A VATSIM ID is 6 or 7 digits");
      await prefsApi.update({
        simbrief_userid: sbUserid || null,
        auto_file_pirep: autoFile,
        share_debriefs: shareDebriefs,
      });
      if (vatsimCid !== (vatsim?.cid || "")) await vatsimApi.set(vatsimCid || null);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["prefs"] });
      qc.invalidateQueries({ queryKey: ["vatsim-id"] });
      toast.success("Settings saved");
    },
    onError: (e) => toast.error(e.response?.data?.detail || e.message || "Save failed"),
  });

  return (
    <div className="p-8 max-w-3xl" data-testid="settings-page">
      <div className="mb-6">
        <div className="overline mb-1">Configuration</div>
        <h1 className="text-3xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Pilot preferences and account info.
        </p>
      </div>

      <section className="card-flat p-6 mb-6" data-testid="settings-simbrief">
        <div className="overline mb-1">Dispatch</div>
        <h2 className="text-base font-bold mb-3">SimBrief profile</h2>
        <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
          VACARS5 pulls your generated OFP directly from SimBrief using your
          Pilot ID. Find it under <span className="font-mono text-primary">simbrief.com → Account Settings</span>.
        </p>
        <label className="block mb-4 max-w-xs">
          <span className="overline block mb-1.5">SimBrief Pilot ID (numeric)</span>
          <input
            type="text"
            value={sbUserid}
            onChange={(e) => setSbUserid(e.target.value.replace(/\D/g, ""))}
            placeholder="e.g. 123456"
            data-testid="settings-simbrief-userid"
            className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm font-mono text-foreground focus:outline-none focus:border-primary"
          />
        </label>
      </section>

      <section className="card-flat p-6 mb-6" data-testid="settings-vatsim">
        <div className="overline mb-1">Network</div>
        <h2 className="text-base font-bold mb-3">VATSIM ID</h2>
        <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
          Your VATSIM member number (CID). The Live Map uses it to show when
          you're connected to VATSIM; without it your flight shows <span className="font-mono">NO ID</span>.
          It's saved on your myVAC record, so myVAC shows it too.
        </p>
        <label className="block max-w-xs">
          <span className="overline block mb-1.5">VATSIM ID (6 or 7 digits)</span>
          <input
            type="text"
            inputMode="numeric"
            value={vatsimCid}
            onChange={(e) => setVatsimCid(e.target.value.replace(/\D/g, "").slice(0, 7))}
            placeholder="e.g. 1234567"
            aria-invalid={vatsimInvalid}
            data-testid="settings-vatsim-cid"
            className={`w-full bg-background border rounded-md px-3 py-2 text-sm font-mono text-foreground focus:outline-none focus:border-primary ${vatsimInvalid ? "border-primary" : "border-border"}`}
          />
        </label>
        {vatsimInvalid && (
          <p className="text-xs text-primary mt-2" data-testid="settings-vatsim-invalid">A VATSIM ID is 6 or 7 digits.</p>
        )}
      </section>

      <section className="card-flat p-6 mb-6" data-testid="settings-debriefs">
        <div className="overline mb-1">Debriefs</div>
        <h2 className="text-base font-bold mb-3">Share my flight debriefs</h2>
        <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
          After each flight VACARS5 makes a debrief card: route, landing, fuel and
          times. With sharing on, its link shows the card to anyone you send it to,
          and pasted in Discord it shows as a picture. The card shows your VAC ID and
          first name. Off, the links stop working; you still see your own debriefs.
        </p>
        <label className="flex items-center gap-3 cursor-pointer" data-testid="settings-share-debriefs">
          <input
            type="checkbox"
            checked={shareDebriefs}
            onChange={(e) => setShareDebriefs(e.target.checked)}
            className="w-4 h-4 accent-primary"
          />
          <span className="text-sm">{shareDebriefs ? "Sharing on" : "Sharing off"}</span>
        </label>
      </section>

      <section className="card-flat p-6 mb-6" data-testid="settings-autofile">
        <div className="overline mb-1">ACARS</div>
        <h2 className="text-base font-bold mb-3">Auto-file PIREP on arrival</h2>
        <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
          Files the flight automatically once you're at the gate with the parking
          brake set and the engines shut down, as in VACARS3. Turn off if you
          want to file manually from Live Tracking.
        </p>
        <label className="flex items-center gap-3 cursor-pointer" data-testid="settings-autofile-toggle">
          <input
            type="checkbox"
            checked={autoFile}
            onChange={(e) => setAutoFile(e.target.checked)}
            className="w-4 h-4 accent-primary"
          />
          <span className="text-sm">{autoFile ? "Enabled" : "Disabled"}</span>
        </label>
      </section>

      {isDesktop && (
        <section className="card-flat p-6 mb-6" data-testid="settings-overlay">
          <div className="overline mb-1">Desktop</div>
          <h2 className="text-base font-bold mb-3">In-sim HUD overlay</h2>
          <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
            Shows a slim always-on-top strip at the top of your screen while a
            flight is INITed — flight number, ORIG → DEST, distance remaining,
            altitude, speed, and your landing rating on touchdown. Click-through,
            so it never steals focus from MSFS / Prepar3D / X-Plane.
          </p>
          <label className="flex items-center gap-3 cursor-pointer" data-testid="settings-overlay-toggle">
            <input
              type="checkbox"
              checked={overlayEnabled}
              onChange={(e) => toggleOverlay(e.target.checked)}
              className="w-4 h-4 accent-primary"
            />
            <span className="text-sm">{overlayEnabled ? "Enabled" : "Disabled"}</span>
          </label>

          <div className="flex flex-wrap gap-2 mt-4">
            <button
              type="button"
              onClick={() => window.electronAPI.overlay?.setMoving?.(true)}
              data-testid="settings-overlay-move"
              className="text-xs font-bold uppercase tracking-widest px-3 py-2 rounded-md border border-border hover:bg-secondary"
            >
              Move overlay
            </button>
            <button
              type="button"
              onClick={() => {
                window.electronAPI.overlay?.resetPosition?.();
                toast.success("Overlay moved back to the top of the screen");
              }}
              data-testid="settings-overlay-reset"
              className="text-xs font-bold uppercase tracking-widest px-3 py-2 rounded-md border border-border hover:bg-secondary"
            >
              Reset position
            </button>
          </div>

          <OverlayHotkeys />
        </section>
      )}

      <TextSizeSection />

      <button
        onClick={() => savePrefs.mutate()}
        disabled={savePrefs.isPending}
        data-testid="settings-save"
        className="mb-6 bg-primary text-primary-foreground text-sm font-bold uppercase tracking-widest px-5 py-2.5 rounded-md hover:brightness-110 flex items-center gap-2 disabled:opacity-60"
      >
        {savePrefs.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
        Save settings
      </button>

      <section className="card-flat p-6" data-testid="settings-account">
        <div className="overline mb-1">Account</div>
        <h2 className="text-base font-semibold mb-4">Pilot record</h2>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <Row label="Pilot ID" value={user?.pid} mono />
          <Row label="Name" value={`${user?.first_name || ""} ${user?.last_name || ""}`} />
          <Row label="Email" value={user?.email} mono />
          <Row label="Hub" value={user?.hub} mono />
          <Row label="Rank" value={card?.rank_title || card?.rank_short || "—"} />
          <Row label="VATSIM" value={user?.vatsim || "—"} mono />
        </div>
      </section>
    </div>
  );
}

// Applies at once and is kept on this computer, so it sits outside Save
// settings (which writes the pilot's record on the server).
function TextSizeSection() {
  const [size, setSize] = useState(getTextSize);
  const desktop = canSetTextSize();
  const isMac = typeof navigator !== "undefined" && /Mac/.test(navigator.platform || "");
  return (
    <section className="card-flat p-6 mb-6" data-testid="settings-text-size">
      <div className="overline mb-1">Display</div>
      <h2 className="text-base font-bold mb-3">Text size</h2>
      {desktop ? (
        <>
          <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
            Makes everything in VACARS5 bigger: text, tables and maps. Kept on
            this computer only, so each screen can have its own.
          </p>
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Text size">
            {TEXT_SIZES.map((s) => (
              <button
                key={s.id}
                type="button"
                role="radio"
                aria-checked={size === s.id}
                onClick={() => { setTextSize(s.id); setSize(s.id); }}
                data-testid={`settings-text-size-${s.id}`}
                className={`text-xs font-bold uppercase tracking-widest px-3 py-2 rounded-md border ${
                  size === s.id
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border hover:bg-secondary"
                }`}
              >
                {s.label} <span className="font-normal opacity-80">{Math.round(s.factor * 100)}%</span>
              </button>
            ))}
          </div>
        </>
      ) : (
        <p className="text-xs text-muted-foreground leading-relaxed">
          In a browser, use its zoom: {isMac ? "Cmd" : "Ctrl"} + to enlarge, {isMac ? "Cmd" : "Ctrl"} − to
          shrink, {isMac ? "Cmd" : "Ctrl"} 0 to reset. The browser remembers it for this site. The
          desktop app has a text size setting here instead.
        </p>
      )}
    </section>
  );
}

function Row({ label, value, mono }) {
  return (
    <div>
      <div className="overline mb-1">{label}</div>
      <div className={`text-sm ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}
